import {
  CloseIcon,
  ElephantEOnlyLogoIcon,
  ElephantLogoIcon,
  HamburgerMenuIcon,
} from "$components/icons";
import { makeStyles } from "$components/mui-styles";
import { useSideMenuContext } from "$components/navigation";
import { NotificationCenter } from "$components/notifications/notification-center";
import {
  type Territory,
  useConfig,
} from "$global-providers/config-provider/provider";
import { featureFlags } from "$global-providers/feature-flags-provider/feature-flags";
import { useFeatureFlag } from "$global-providers/feature-flags-provider/provider";
import { useLazyUser } from "$global-providers/user-provider";
import { MEDIA_QUERY_SMALL_DEVICE } from "$routes/_lib/constants";
import { linkToHome } from "$routes/_lib/links";
import {
  AppBar,
  Box,
  IconButton,
  styled,
  Toolbar,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import type { Role } from "elephant-web/gql";
import React, { type ComponentProps, useRef } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";
import { AppOutOfDateBanner } from "./app-out-of-date-banner";
import { type Flags, LeftSideMenu } from "./side-menu/side-menu";
import { UnstableConnectionBanner } from "./unstable-connection-banner";
import { UnsyncedToast } from "./unsynced-toast";
import { UserDetails } from "./user-details";

export const Header: FC<{
  centerContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  offlineStatus?: ComponentProps<typeof BaseHeader>["offlineStatus"];
}> = ({
  centerContent,
  rightContent = <UserDetails />,
  offlineStatus,
  ...props
}) => {
  const [immunisationRegisterEnabled] = useFeatureFlag(
    featureFlags.immunisations.register,
  );
  const [notificationCenterEnabled] = useFeatureFlag(
    featureFlags.notifications.center,
  );

  // useLazyUser will return null if there was an issue loading the user
  // We use this instead of useUserContext as ErrorPage would still render this Header
  // if an error was thrown while loading user details
  const user = useLazyUser();
  const { territory } = useConfig();

  if (!user) return null;

  return (
    <BaseHeader
      {...props}
      centerContent={centerContent}
      rightContent={rightContent}
      featureFlags={{ immunisationRegisterEnabled }}
      userRole={user.role}
      territory={territory}
      facilityId={user.facilityId}
      organisationId={user.organisationId}
      offlineStatus={offlineStatus}
      notificationCenterEnabled={notificationCenterEnabled}
    />
  );
};

const useBurgerMenuButtonStyles = makeStyles((theme) => ({
  button: {
    marginLeft: -12,
    marginRight: 20,
    [theme.breakpoints.down("sm")]: {
      marginRight: 0,
    },
  },
}));

const BurgerMenuButton = () => {
  const classes = useBurgerMenuButtonStyles();
  const { formatMessage } = useIntl();
  const { isOpen, closable, openLeftMenu, closeMenu } = useSideMenuContext();

  return (
    <IconButton
      data-testid="Header.ShowHideMenuIcon"
      aria-label={formatMessage({
        id: "header.menu.toggle",
        defaultMessage: "Toggle Menu",
      })}
      className={classes.button}
      onClick={() => {
        if (isOpen && closable) {
          closeMenu();
        } else if (!isOpen) {
          openLeftMenu();
        }
      }}
    >
      {isOpen && closable ? (
        <CloseIcon data-testid="Header.HideMenuIcon" />
      ) : (
        <HamburgerMenuIcon data-testid="Header.ShowMenuIcon" />
      )}
    </IconButton>
  );
};

/**
 * Renders the Elephant logo as a home link
 * On mobile, if there is rightContent, this will render the small "e" logo
 * Else, this will render the full "elephant" logo
 */
const HomeIconLink = ({ hasRightContent }: { hasRightContent: boolean }) => {
  const theme = useTheme();
  const fill = theme.palette.common.white;
  const smallWidthDevice = useMediaQuery(MEDIA_QUERY_SMALL_DEVICE);

  return (
    <Link to={linkToHome()} style={{ display: "flex" }} data-testid="home-link">
      {smallWidthDevice && hasRightContent ? (
        <ElephantEOnlyLogoIcon fill={fill} />
      ) : (
        <ElephantLogoIcon fill={fill} />
      )}
    </Link>
  );
};

const HeaderContent = styled(Toolbar)(({ theme }) => ({
  display: "flex",
  alignContent: "center",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "8px",
  width: "100%",
}));

const Section = styled(Box)({
  flex: 1,
});

const MiddleSection = styled(Section)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const RightSection = styled(Section)({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  gap: 8,
});

const RightPortalContent = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
  [theme.breakpoints.down("xs")]: {
    display: "none",
  },
}));

const Bar = styled(AppBar)(({ theme }) => ({
  backgroundColor: theme.palette.brand.main,
}));

/**
 * For the sake of a very rapid fix, this component chooses to hide first the middle portal and then the right portal as the screen size decreases.
 */
export const BaseHeader: FC<{
  featureFlags: Flags;
  userRole?: Role;
  organisationId: string;
  facilityId: string;
  territory: Territory;
  centerContent?: React.ReactNode;
  rightContent?: React.ReactNode;
  className?: string;
  offlineStatus?: "online" | "offline" | "initiallyOffline";
  notificationCenterEnabled?: boolean;
}> = ({
  centerContent,
  rightContent,
  featureFlags,
  userRole,
  territory,
  organisationId,
  facilityId,
  className,
  offlineStatus,
  notificationCenterEnabled,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const showUnstableConnectionBanner = offlineStatus !== "online";

  const Banners = () => (
    <div>
      {showUnstableConnectionBanner && (
        <UnstableConnectionBanner
          isInitiallyOffline={offlineStatus === "initiallyOffline"}
        />
      )}
      {<AppOutOfDateBanner />}
    </div>
  );

  return (
    <Bar
      elevation={0}
      data-testid="GlobalHeader"
      ref={ref}
      className={className}
    >
      <LeftSideMenu
        featureFlags={featureFlags}
        userRole={userRole}
        territory={territory}
        organisationId={organisationId}
        facilityId={facilityId}
      />
      <HeaderContent>
        <Section display="flex" alignItems="center" flex={1}>
          <BurgerMenuButton />
          <HomeIconLink hasRightContent={!!rightContent} />
        </Section>
        <MiddleSection>{centerContent}</MiddleSection>
        <RightSection>
          {notificationCenterEnabled && <NotificationCenter />}
          <RightPortalContent>{rightContent ?? <div />}</RightPortalContent>
        </RightSection>
      </HeaderContent>
      <Banners />
      <UnsyncedToast />
    </Bar>
  );
};
